import { parseLeadRequirementDetails, type LeadRequirementDetails } from '../crm/requirementDetails';
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { computeConstructionProgress } from '../lib/constructionProgress';
import { generateNextId, batchInsertWithCustomId } from '../lib/idGenerator';
import type { Project, WorkType, ProjectCategory, ProjectPriority, DesignServiceType, DesignService, StatusComment, Phase, Customer, User, Todo, CreateTodoData, UpdateTodoData } from '../types';
import { constructionPhases } from '../data/constructionPhaseTemplate';
import { useAuthStore } from '../store/authStore';
import { createInitialProjectFolders } from '../lib/awsS3Explorer';
import { normalizeProjectManagerAssignments } from '../lib/projectManagerDisplay';

interface ProjectsState {
  projects: Project[];
  selectedProject: Project | null;
  customers: Customer[];
  projectManagers: User[];
  loading: boolean;
  error: string | null;
  /**
   * True once a fetch has COMPLETED — success or failure, rows or none.
   *
   * Callers used to gate on `projects.length === 0 && !loading`, which is only
   * a valid "not loaded yet" test when the user can actually see projects. For
   * anyone whose result is legitimately empty (a new custom role sees no
   * projects at all, since project RLS keys off the base tier) the condition
   * stayed true after every fetch, and with `loading` in the effect's deps the
   * completion itself re-ran the effect — an endless refetch loop that also
   * kept the dashboard pinned in its loading state.
   */
  hasLoaded: boolean;

  fetchProjects: () => Promise<void>;
  addProject: (projectData: CreateProjectData) => Promise<Project | undefined>;
  updateProject: (id: number, updates: UpdateProjectData) => Promise<void>;
  deleteProject: (id: number) => Promise<void>;
  setSelectedProject: (project: Project | null) => void;
  setCustomers: (customers: Customer[]) => void;
  setUsers: (users: User[]) => void;
  
  getProjectById: (id: number) => Promise<Project | undefined>;
  updatePhase: (projectId: number, phaseName: string, updates: PhaseUpdate) => Promise<void>;
  updateService: (projectId: number, serviceType: string, updates: ServiceUpdate) => Promise<void>;
  updateProjectStatus: (projectId: number, newStatus: Project['status'], comment: string, userId: string, userName: string, endDate?: Date | null) => Promise<void>;
  addTodo: (todoData: CreateTodoData) => Promise<void>;
  updateTodo: (todoId: number, updates: UpdateTodoData) => Promise<void>;
  deleteTodo: (todoId: number) => Promise<void>;
}

interface CreateProjectData {
  name: string;
  type: WorkType;
  customerId: number;
  customerName: string;
  managerId: string; // Keep for backward compatibility
  managerName: string; // Keep for backward compatibility
  managerIds?: string[]; // Add this for multiple managers
  startDate: Date;
  description: string;
  location: string;
  // New site-specific fields
  cornerSite?: boolean;
  buildType?: string; // Using existing buildtype column
  projectCategory?: ProjectCategory; // Using existing project_type column
  siteDimension?: string;
  doorFacing?: string;
  openKitchen?: boolean | null;
  parking?: boolean | null;
  priority?: ProjectPriority;
  designServices?: Omit<DesignService, 'id'>[];
  budget?: number;
  /** When explicitly false, seeds legacy flat construction_phases; omit or true skips (preferred for new builds). */
  skipLegacyPhases?: boolean;
  latitude?: number | null;
  longitude?: number | null;
}

interface UpdateProjectData {
  name?: string;
  customerId?: number;
  customerName?: string;
  managerId?: string; // Keep for backward compatibility
  managerName?: string; // Keep for backward compatibility
  managerIds?: string[]; // Add this for multiple managers
  startDate?: Date;
  description?: string;
  location?: string;
  latitude?: number | null;
  longitude?: number | null;
  // New site-specific fields
  cornerSite?: boolean;
  buildType?: string; // Using existing buildtype column
  projectCategory?: ProjectCategory; // Using existing project_type column
  siteDimension?: string;
  doorFacing?: string;
  openKitchen?: boolean | null;
  parking?: boolean | null;
  priority?: ProjectPriority;
  designServices?: DesignService[];
  status?: Project['status'];
  statusHistory?: StatusComment[];
  progress?: number;
  spent?: number;
  budget?: number;
  phases?: Phase[];
  endDate?: Date | null;
  completedDate?: Date | null;
  deliveredAt?: Date | null;
  /** CRM structured requirement snapshot; set null to clear. */
  requirementDetails?: LeadRequirementDetails | Record<string, unknown> | null;
}

interface PhaseUpdate {
  status?: Phase['status'];
  completed_date?: string | null;
}

function buildPartialProjectPatch(updates: UpdateProjectData): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (updates.name !== undefined) patch.name = updates.name;
  if (updates.customerId !== undefined) patch.customer_id = updates.customerId;
  if (updates.customerName !== undefined) patch.customer_name = updates.customerName;
  if (updates.managerId !== undefined) patch.manager_id = updates.managerId;
  if (updates.managerName !== undefined) patch.manager_name = updates.managerName;
  if (updates.startDate !== undefined) patch.start_date = updates.startDate?.toISOString();
  if (updates.description !== undefined) patch.description = updates.description;
  if (updates.location !== undefined) patch.location = updates.location;
  if (updates.cornerSite !== undefined) patch.corner_site = updates.cornerSite;
  if (updates.buildType !== undefined) patch.buildtype = updates.buildType;
  if (updates.projectCategory !== undefined) patch.project_type = updates.projectCategory;
  if (updates.siteDimension !== undefined) patch.site_dimension = updates.siteDimension;
  if (updates.doorFacing !== undefined) patch.door_facing = updates.doorFacing;
  if (updates.openKitchen !== undefined) patch.open_kitchen = updates.openKitchen;
  if (updates.parking !== undefined) patch.parking = updates.parking;
  if (updates.priority !== undefined) patch.priority = updates.priority;
  if (updates.status !== undefined) patch.status = updates.status;
  if (updates.budget !== undefined) patch.budget = updates.budget;
  if (updates.progress !== undefined) patch.progress = updates.progress;
  if (updates.spent !== undefined) patch.spent = updates.spent;
  if (updates.endDate !== undefined) patch.end_date = updates.endDate?.toISOString() ?? null;
  if (updates.expectedDate !== undefined) {
    patch.expected_date = updates.expectedDate
      ? updates.expectedDate.toISOString().slice(0, 10)
      : null;
  }
  if (updates.deliveredAt !== undefined) patch.delivered_at = updates.deliveredAt?.toISOString() ?? null;
  if (updates.latitude !== undefined) patch.latitude = updates.latitude;
  if (updates.longitude !== undefined) patch.longitude = updates.longitude;
  if (updates.requirementDetails !== undefined) {
    if (updates.requirementDetails === null) {
      patch.requirement_details = null;
    } else {
      const o = updates.requirementDetails as Record<string, unknown>;
      patch.requirement_details = Object.keys(o).length === 0 ? null : o;
    }
  }
  return patch;
}

const PROJECT_MANAGER_PAGE_SIZE = 1000;

/**
 * Every project_managers row, across as many requests as it takes.
 *
 * The list view builds each project's manager list (and from it, who may see the
 * project) from this table. A bare `.select('*')` is silently capped by PostgREST
 * (1000 rows by default) with no error, so once the table outgrew that cap the
 * overflow rows just vanished from the list — and since every manager edit used to
 * delete + re-insert a project's rows, the freshest assignments were exactly the ones
 * that got cut off. Pages on the exact count (not on "short page") so it's correct
 * even if the server's own cap is lower than our page size.
 */
async function fetchAllProjectManagerRows(): Promise<{
  data: Record<string, unknown>[];
  error: { message: string } | null;
}> {
  const rows: Record<string, unknown>[] = [];
  let total: number | null = null;

  // Resume from however many rows have actually arrived (not from a fixed page stride), so a
  // server-side cap smaller than our page size can't make us skip over rows.
  for (;;) {
    const from = rows.length;
    const { data, error, count } = await supabase
      .from('project_managers')
      .select('*', total === null ? { count: 'exact' } : undefined)
      .order('id', { ascending: true })
      .range(from, from + PROJECT_MANAGER_PAGE_SIZE - 1);

    if (error) return { data: rows, error };
    if (total === null) total = count ?? 0;

    rows.push(...((data ?? []) as Record<string, unknown>[]));
    if (!data || data.length === 0 || rows.length >= total) break;
  }

  return { data: rows, error: null };
}

interface ServiceUpdate {
  status?: DesignService['status'];
  start_date?: string | null;
  end_date?: string | null;
  due_date?: string | null;
  completed_date?: string | null;
  revision_count?: number;
  number_of_times?: number;
}

const createDesignServices = async (projectId: number, selectedServiceTypes?: DesignServiceType[], serviceManagers?: { type: DesignServiceType; managerId: string }[]) => {
  // If selectedServiceTypes is provided, use only those types
  // Otherwise fall back to all service types (for backward compatibility)
  const serviceTypesToCreate = selectedServiceTypes || [
    'floor_plan',
    '3d_elevation',
    'interior_design',
    '3d_working_elevation',
    'structural_design',
    'electrical_design',
    'plumbing_design',
    'hvac_design',
    'sanction_plan',
    'site_visit'
  ];
  
  const designServices = serviceTypesToCreate.map(type => {
    const manager = serviceManagers?.find(sm => sm.type === type);
    return {
      project_id: projectId,
      type,
      status: 'not_started' as const,
      manager_id: manager?.managerId || null
    };
  });

  // Use batchInsertWithCustomId instead of direct insert
  await batchInsertWithCustomId('design_services', designServices);
};

const createConstructionPhases = async (projectId: number) => {
  const phasesToCreate = constructionPhases.flatMap(category => 
    category.phases.map(phase => ({
      project_id: projectId,
      name: phase.name,
      category: category.category,
      duration: phase.duration,
      status: 'not_started' as const
    }))
  );

  // Use batchInsertWithCustomId instead of direct insert
  await batchInsertWithCustomId('construction_phases', phasesToCreate);
};

const initializeServices = (projectType: string) => {
  if (projectType === 'construction') {
    return constructionPhases.map(phase => ({
      type: phase,
      status: 'pending',
      managerId: null
    }));
  } else if (projectType === 'design') {
    return [
      { type: 'concept', status: 'pending', managerId: null },
      { type: 'schematic', status: 'pending', managerId: null },
      { type: 'design_development', status: 'pending', managerId: null },
      { type: 'construction_documents', status: 'pending', managerId: null }
    ];
  }
  return [];
};

/** Shared across callers so concurrent fetches dedupe rather than stacking. */
let inFlightProjectsFetch: Promise<void> | null = null;

export const useProjectsStore = create<ProjectsState>((set, get) => ({
  projects: [],
  selectedProject: null,
  customers: [],
  projectManagers: [],
  loading: false,
  error: null,
  hasLoaded: false,

  fetchProjects: async () => {
    // Collapse overlapping calls into one request. AppInitializer and whichever
    // page mounted first both ask on load; without this they each fire the six
    // queries below.
    if (inFlightProjectsFetch) return inFlightProjectsFetch;

    const run = (async () => {
    try {
      set({ loading: true, error: null });

      // These 6 queries are independent of each other (only combined afterward, in
      // processedProjects below) - fired concurrently instead of one-after-another to avoid
      // a 6-round-trip waterfall on every app load.
      const [
        { data: projects, error },
        { data: projectManagers, error: pmError },
        { data: designServices, error: dsError },
        { data: constructionPhases, error: cpError },
        { data: statusHistory, error: shError },
        { data: todos, error: todosError },
      ] = await Promise.all([
        supabase.from('projects').select('*').order('created_at', { ascending: false }),
        // Fetched separately since the join isn't working. Paged: a plain select is silently
        // capped by PostgREST, which drops the newest manager assignments from the list.
        fetchAllProjectManagerRows(),
        supabase.from('design_services').select('*'),
        supabase.from('construction_phases').select('*'),
        supabase.from('project_status_history').select('*').order('created_at', { ascending: false }),
        supabase.from('project_todos').select('*').order('created_at', { ascending: false }),
      ]);

      if (error) {
        console.error('Error fetching projects:', error);
        set({ error: error.message, loading: false, hasLoaded: true });
        throw error;
      }

      if (pmError) {
        console.error('Error fetching project managers:', pmError);
      }

      const pmUserIds = [
        ...new Set(
          (projectManagers ?? [])
            .map((pm: { user_id?: string }) => pm.user_id)
            .filter((id): id is string => !!id),
        ),
      ];
      let pmUserNames: Record<string, string> = {};
      if (pmUserIds.length > 0) {
        const { data: pmUsers, error: pmUsersError } = await supabase
          .from('users')
          .select('id, name')
          .in('id', pmUserIds);
        if (pmUsersError) {
          console.error('Error fetching project manager user names:', pmUsersError);
        } else {
          pmUserNames = (pmUsers ?? []).reduce((acc: Record<string, string>, user: { id: string; name: string }) => {
            acc[user.id] = user.name;
            return acc;
          }, {});
        }
      }

      if (dsError) {
        console.error('Error fetching design services:', dsError);
      }

      if (cpError) {
        console.error('Error fetching construction phases:', cpError);
      }

      if (shError) {
        console.error('Error fetching status history:', shError);
      }

      if (todosError) {
        console.error('Error fetching todos:', todosError);
      }

      // Optimize data processing with more efficient filtering
      const processedProjects = projects.map((project: any) => {
        // Use more efficient filtering with early returns
        const projectId = project.id;
        
        // Assign design services to the project
        const projectDesignServices = designServices?.filter((ds: any) => ds.project_id === projectId) || [];
        
        // Assign construction phases to the project
        const projectPhases = constructionPhases?.filter((cp: any) => cp.project_id === projectId) || [];
        
        // Assign status history to the project
        const projectStatusHistory = statusHistory
          ? statusHistory
              .filter((sh: any) => sh.project_id === projectId)
              .map((sh: any) => ({
                id: sh.id,
                status: sh.status,
                comment: sh.comment,
                userId: sh.user_id,
                userName: sh.user_name,
                timestamp: new Date(sh.created_at)
              }))
          : [];

        // Assign todos to the project
        const projectTodos = todos
          ? todos
              .filter((todo: any) => todo.project_id === projectId)
              .map((todo: any) => ({
                id: todo.id,
                projectId: todo.project_id,
                title: todo.title,
                description: todo.description,
                dueDate: todo.due_date ? new Date(todo.due_date) : undefined,
                priority: todo.priority,
                status: todo.status,
                createdBy: todo.created_by,
                assignedTo: todo.assigned_to ?? null,
                createdAt: new Date(todo.created_at),
                updatedAt: new Date(todo.updated_at),
                completedAt: todo.completed_at ? new Date(todo.completed_at) : undefined,
                notes: todo.notes
              }))
          : [];
        
        // Assign project managers to the project
        const managersByProject = projectManagers?.filter((pm: any) => pm.project_id === projectId) || [];

        // Create the formatted project object with all required properties
        return {
          id: project.id,
          name: project.name,
          type: project.type,
          status: project.status,
          customerId: project.customer_id,
          customerName: project.customer_name,
          managerId: project.manager_id,
          managerName: project.manager_name,
          startDate: new Date(project.start_date),
          endDate: project.end_date ? new Date(project.end_date) : null,
          expectedDate: project.expected_date ? new Date(project.expected_date) : null,
          deliveredAt: project.delivered_at ? new Date(project.delivered_at) : null,
          completedDate: project.completed_date ? new Date(project.completed_date) : null,
          description: project.description,
          requirementDetails: project.requirement_details
            ? parseLeadRequirementDetails(project.requirement_details)
            : null,
          location: project.location,
          latitude: project.latitude != null ? Number(project.latitude) : null,
          longitude: project.longitude != null ? Number(project.longitude) : null,
          // New site-specific fields
          cornerSite: project.corner_site,
          buildType: project.buildtype, // Using existing buildtype column
          projectCategory: project.project_type, // Using existing project_type column
          siteDimension: project.site_dimension,
          doorFacing: project.door_facing,
          openKitchen: project.open_kitchen,
          parking: project.parking,
          priority: project.priority,
          referenceNumber: project.reference_number ?? null,
          createdAt: new Date(project.created_at),
          updatedAt: project.updated_at ? new Date(project.updated_at) : new Date(project.created_at),
          budget: project.budget || 0,
          progress: project.progress || 0,
          spent: project.spent || 0,
          designServices: projectDesignServices.map((ds: any) => ({
            id: ds.id,
            type: ds.type,
            status: ds.status || 'not_started',
            managerId: ds.manager_id || undefined,
            managerName: ds.manager_name || undefined,
            startDate: ds.start_date ? new Date(ds.start_date) : undefined,
            dueDate: ds.due_date ? new Date(ds.due_date) : undefined,
            endDate: ds.end_date ? new Date(ds.end_date) : undefined,
            completedDate: ds.completed_date ? new Date(ds.completed_date) : null,
            comments: []
          })),
          phases: projectPhases.map((phase: any) => ({
            id: phase.id,
            name: phase.name,
            category: phase.category || '',
            duration: phase.duration || '',
            status: phase.status || 'not_started',
            startDate: phase.start_date ? new Date(phase.start_date) : null,
            endDate: phase.end_date ? new Date(phase.end_date) : null,
            completedDate: phase.completed_date ? new Date(phase.completed_date) : null,
            documents: [],
            expenses: [],
            comments: [],
            possibleStatuses: ['not_started', 'in_progress', 'on_hold', 'completed']
          })),
          statusHistory: projectStatusHistory,
          todos: projectTodos,
          documents: project.documents || [],
          projectManagers: normalizeProjectManagerAssignments(
            managersByProject.map((pm: any) => ({
              id: pm.id,
              userId: pm.user_id,
              name: pmUserNames[pm.user_id] || project.manager_name || 'Unknown',
              isPrimary: pm.is_primary === true,
              assignedAt: pm.created_at ? new Date(pm.created_at) : new Date(),
            })),
            project.manager_id,
          ),
        };
      });

      // Fix: Ensure all required Project fields are present to avoid type errors
      // Add missing customerNumber and overallProjectStatus with default or derived values
      const completedProjects = processedProjects.map((project: any) => ({
        ...project,
        customerNumber: project.customerNumber ?? '', // Default to empty string if missing
        overallProjectStatus: project.overallProjectStatus ?? project.status // Use status as fallback
      }));

      set({ projects: completedProjects, loading: false, hasLoaded: true });
    } catch (error) {
      console.error('Error in fetchProjects:', error);
      // hasLoaded is set on failure too: one attempt, then stop. Retrying is the
      // user's call (the error surfaces in the UI), not an automatic loop.
      set({
        loading: false,
        hasLoaded: true,
        error: error instanceof Error ? error.message : 'Could not load projects.',
      });
      throw error;
    } finally {
      inFlightProjectsFetch = null;
    }
    })();

    inFlightProjectsFetch = run;
    return run;
  },

  addProject: async (projectData: CreateProjectData) => {
    set({ loading: true, error: null });
    try {
      
      const p_fields = {
        name: projectData.name,
        type: projectData.type,
        customer_id: projectData.customerId,
        customer_name: projectData.customerName ?? '',
        manager_id: projectData.managerId,
        manager_name: projectData.managerName ?? '',
        start_date: projectData.startDate.toISOString(),
        expected_date: projectData.expectedDate
          ? projectData.expectedDate.toISOString().slice(0, 10)
          : null,
        description: projectData.description ?? '',
        location: projectData.location ?? '',
        latitude: projectData.latitude ?? null,
        longitude: projectData.longitude ?? null,
        corner_site: projectData.cornerSite ?? null,
        buildtype: projectData.buildType ?? '',
        project_type: projectData.projectCategory ?? '',
        site_dimension: projectData.siteDimension ?? '',
        door_facing: projectData.doorFacing ?? '',
        open_kitchen: projectData.openKitchen ?? null,
        parking: projectData.parking ?? null,
        priority: projectData.priority || 'not_specified',
        budget: projectData.budget || 1,
        status: 'planning'
      };

      const { data: rawProjectId, error: rpcError } = await supabase.rpc('create_staff_project', {
        p_fields
      });

      if (rpcError) {
        console.error('Failed to create project via create_staff_project:', rpcError);
        throw rpcError;
      }

      const projectId =
        typeof rawProjectId === 'number' ? rawProjectId : Number(rawProjectId);

      if (!Number.isFinite(projectId) || projectId <= 0) {
        throw new Error('Project created but returned invalid id');
      }

      // Create S3 folder structure asynchronously (don't wait for it)
      (async () => {
        try {
          await createInitialProjectFolders(Number(projectId));
        } catch (s3Error) {
          console.error('Failed to create S3 folder structure:', s3Error);
        }
      })();
      
      // Assign all the selected managers to the project
      // Ensure no duplicates in managerIds and primary manager is set correctly
      const uniqueManagerIds = new Set<string>();
      
      // Add primary manager first
      uniqueManagerIds.add(projectData.managerId);
      
      // Add additional managers if provided
      if (projectData.managerIds && projectData.managerIds.length > 0) {
        projectData.managerIds.forEach(id => {
          if (id !== projectData.managerId) { // Avoid duplicating the primary manager
            uniqueManagerIds.add(id);
          }
        });
      }
      
      const managerEntries = Array.from(uniqueManagerIds).map(userId => ({
        project_id: projectId,
        user_id: userId,
        is_primary: userId === projectData.managerId // Explicitly set primary manager
      }));
      
      try {
        // Insert with is_primary flag to distinguish the primary manager
        const { error: assignError } = await supabase
          .from('project_managers')
          .insert(managerEntries)
          .select();
          
        if (assignError) {
          console.error('Error assigning project managers:', assignError);
        }
      } catch (assignError) {
        console.error('Failed to assign project managers:', assignError);
        // Continue with project creation even if manager assignment fails
      }

      // Initialize appropriate services based on project type
      try {
        if (projectData.type === 'construction' && projectData.skipLegacyPhases === false) {
          await createConstructionPhases(projectId);
        } else if (projectData.type === 'construction' && projectData.skipLegacyPhases) {
          // New construction projects use the flexible builder - skip legacy seeding
        } else if (projectData.type === 'design') {
          // Extract service types and managers
          const selectedServiceTypes = projectData.designServices?.map(service => service.type);
          const serviceManagers = projectData.designServices?.map(service => ({
            type: service.type,
            managerId: service.managerId || ''
          }));
          await createDesignServices(projectId, selectedServiceTypes, serviceManagers);
        }
      } catch (servicesError) {
        console.error('Error initializing project services:', servicesError);
        // Continue with project creation even if services initialization fails
      }

      // Get the created project with all its details
      let newProject;
      try {
        newProject = await get().getProjectById(projectId);
        if (!newProject) throw new Error('Failed to fetch created project');
      } catch (fetchError) {
        console.error('Error fetching created project:', fetchError);
        throw new Error('Project was created but failed to retrieve details. Please refresh the page.');
      }

      set(state => ({ 
        projects: [newProject, ...state.projects],
        loading: false 
      }));

      return newProject;

    } catch (error) {
      console.error('Error in addProject:', error);
      
      // Generate a more helpful error message
      let errorMessage = 'An unexpected error occurred while creating the project.';
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          errorMessage = 'Network connection error. Please check your internet connection and try again.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'The server took too long to respond. Please try again later.';
        } else {
          errorMessage = error.message;
        }
      }
      
      set({ error: errorMessage, loading: false });
      throw new Error(errorMessage);
    }
  },

  updateProject: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      const projectPatch = buildPartialProjectPatch(updates);

      let projectType: string | undefined;

      if (Object.keys(projectPatch).length > 0) {
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .update(projectPatch)
          .eq('id', id)
          .select()
          .single();

        if (projectError) throw projectError;
        projectType = project?.type;
      } else {
        const { data: row, error: rowError } = await supabase
          .from('projects')
          .select('type')
          .eq('id', id)
          .single();
        if (rowError) throw rowError;
        projectType = row?.type;
      }

      // If manager assignments were updated
      if (updates.managerIds && updates.managerIds.length > 0) {
        const primaryId = updates.managerId ?? updates.managerIds[0];
        try {
          let primaryName = updates.managerName;
          if (primaryId && !primaryName) {
            const { data: primaryUser } = await supabase
              .from('users')
              .select('name')
              .eq('id', primaryId)
              .maybeSingle();
            primaryName = primaryUser?.name ?? '';
          }

          if (primaryId) {
            const { error: primaryPatchError } = await supabase
              .from('projects')
              .update({
                manager_id: primaryId,
                manager_name: primaryName ?? '',
              })
              .eq('id', id);
            if (primaryPatchError) throw primaryPatchError;
          }

          const uniqueManagerIds = new Set<string>();
          if (primaryId) uniqueManagerIds.add(primaryId);
          updates.managerIds.forEach((userId) => uniqueManagerIds.add(userId));

          const managerEntries = Array.from(uniqueManagerIds).map((userId) => ({
            project_id: id,
            user_id: userId,
            is_primary: primaryId ? userId === primaryId : false,
          }));

          // Upsert the desired set FIRST, then drop only the managers that were removed. This used
          // to delete every row and re-insert them, so any failure between the two steps left the
          // project with no managers at all, and every save re-created all rows (new ids) even
          // when nothing about a given manager had changed.
          const { error: assignError } = await supabase
            .from('project_managers')
            .upsert(managerEntries, { onConflict: 'project_id,user_id' });

          if (assignError) {
            console.error('Error assigning project managers:', assignError);
            throw assignError;
          }

          const { data: assignedRows, error: readBackError } = await supabase
            .from('project_managers')
            .select('id, user_id')
            .eq('project_id', id);

          if (readBackError) {
            console.error('Error verifying project managers:', readBackError);
            throw readBackError;
          }

          // Read-back check: the write call can come back without an error while rows still
          // aren't there to be read (e.g. a policy filtering them), which previously surfaced as a
          // "saved successfully" message with the assignment silently missing. Fail loudly instead.
          const savedUserIds = new Set((assignedRows ?? []).map((row) => row.user_id as string));
          const notSaved = managerEntries.filter((entry) => !savedUserIds.has(entry.user_id));
          if (notSaved.length > 0) {
            throw new Error(
              `${notSaved.length} project manager assignment(s) did not save. ` +
                'Nothing was removed — please retry, and check your permissions on this project if it keeps happening.',
            );
          }

          const idsToRemove = (assignedRows ?? [])
            .filter((row) => !uniqueManagerIds.has(row.user_id as string))
            .map((row) => row.id as number);

          if (idsToRemove.length > 0) {
            const { error: removeError } = await supabase
              .from('project_managers')
              .delete()
              .in('id', idsToRemove);

            if (removeError) {
              console.error('Error removing project managers:', removeError);
              throw removeError;
            }
          }
        } catch (managerError) {
          console.error('Failed to update project managers:', managerError);
          throw managerError;
        }
      }

      // If design services were updated
      if (updates.designServices && projectType === 'design') {
        console.log('=== UPDATE PROJECT DEBUG ===');
        console.log('Processing design services update for project:', id);
        console.log('Design services to process:', updates.designServices);
        console.log('Project type:', projectType);
        
        try {
          // Fetch existing services first
          const { data: existingServices, error: fetchServicesError } = await supabase
            .from('design_services')
            .select('*')
            .eq('project_id', id);
            
          if (fetchServicesError) {
            console.error('Error fetching existing design services:', fetchServicesError);
            throw fetchServicesError;
          }
          
          console.log('Existing services from database:', existingServices);
          

          
          // Map services by type for easier comparison
          const existingServicesByType = (existingServices || []).reduce((acc: any, service: any) => {
            acc[service.type] = service;
            return acc;
          }, {});
          
          // Identify services to keep, update, or add
          const servicesToUpdate: any[] = [];
          const servicesToAdd: any[] = [];
          
          console.log('Existing services by type:', existingServicesByType);
          
          updates.designServices.forEach(service => {
            const existingService = existingServicesByType[service.type];
            console.log(`Processing service ${service.type}:`, service);
            console.log(`Existing service found:`, existingService);
            
            if (existingService) {
              // Update existing service
              console.log(`Will UPDATE service ${service.type}`);
              const existingCompletedDateIso = existingService.completed_date
                ? new Date(existingService.completed_date).toISOString()
                : null;

              const serviceCompletedDateIso = service.completedDate
                ? new Date(service.completedDate).toISOString()
                : null;

              const completedDateToPersist =
                service.status === 'completed'
                  ? (serviceCompletedDateIso || existingCompletedDateIso || new Date().toISOString())
                  : null;

              servicesToUpdate.push({
                id: existingService.id,
                project_id: id,
                type: service.type,
                status: service.status,
                manager_id: service.managerId || null,
                start_date: service.startDate ? new Date(service.startDate).toISOString() : existingService.start_date,
                end_date: service.endDate ? new Date(service.endDate).toISOString() : existingService.end_date,
                completed_date: completedDateToPersist,
                revision_count: service.type === 'site_visit' ? 1 : (service.revisionCount || existingService.revision_count || 1),
                number_of_times: service.type === 'site_visit' ? (service.numberOfTimes || existingService.number_of_times || 1) : 1
              });
              
              // Remove from existingServicesByType to track what's left to delete
              delete existingServicesByType[service.type];
            } else {
              // Add as new service
              console.log(`Will ADD new service ${service.type}`);
              const serviceCompletedDateIso = service.completedDate
                ? new Date(service.completedDate).toISOString()
                : null;

              servicesToAdd.push({
                project_id: id,
                type: service.type,
                status: service.status,
                manager_id: service.managerId || null,
                start_date: service.startDate ? new Date(service.startDate).toISOString() : null,
                end_date: service.endDate ? new Date(service.endDate).toISOString() : null,
                completed_date:
                  service.status === 'completed'
                    ? (serviceCompletedDateIso || new Date().toISOString())
                    : null,
                revision_count: service.type === 'site_visit' ? 1 : (service.revisionCount || 1),
                number_of_times: service.type === 'site_visit' ? (service.numberOfTimes || 1) : 1
              });
            }
          });
          
          console.log('Services to update:', servicesToUpdate);
          console.log('Services to add:', servicesToAdd);
          
          // Identify services to delete (those remaining in existingServicesByType)
          const servicesToDeleteIds = Object.values(existingServicesByType).map((service: any) => service.id);
          

          
          // Process updates sequentially instead of parallel to better handle errors
          try {
            // 1. Delete services that are no longer needed
            if (servicesToDeleteIds.length > 0) {
              const { error: deleteError } = await supabase
                .from('design_services')
                .delete()
                .in('id', servicesToDeleteIds);
              
              if (deleteError) {
                console.error('Error deleting design services:', deleteError);
                throw deleteError;
              }
            }
            
            // 2. Update existing services one by one to isolate any problematic updates
            for (const service of servicesToUpdate) {
              // Ensure we're only sending valid fields to the API
              const serviceUpdateData = {
                type: service.type,
                status: service.status,
                manager_id: service.manager_id || null,
                // Handle dates properly, ensuring they're in ISO format or null
                start_date: service.start_date || null,
                end_date: service.end_date || null,
                completed_date: service.completed_date || null,
                revision_count: service.revision_count || 1,
                number_of_times: service.number_of_times || 1,
                ...(service.status === 'completed' ? { due_date: null as string | null } : {}),
              };
              
              const { error: serviceUpdateError } = await supabase
                .from('design_services')
                .update(serviceUpdateData)
                .eq('id', service.id);
              
              if (serviceUpdateError) {
                console.error(`Error updating design service ${service.id}:`, serviceUpdateError);
                throw serviceUpdateError;
              }
            }
            
            // 3. Add new services as a batch
            if (servicesToAdd.length > 0) {
              console.log('=== ADDING NEW SERVICES ===');
              console.log('Services to add count:', servicesToAdd.length);
              console.log('Adding new design services:', servicesToAdd);
              // Ensure each service object has consistent field names
              const cleanedServicesToAdd = servicesToAdd.map(service => ({
                project_id: id,
                type: service.type,
                status: service.status,
                manager_id: service.manager_id || null,
                start_date: service.start_date || null,
                end_date: service.end_date || null,
                completed_date: service.completed_date || null,
                revision_count: service.revision_count || 1,
                number_of_times: service.number_of_times || 1
              }));
              
              console.log('Cleaned services to add:', cleanedServicesToAdd);

              // Use explicit IDs from MAX(id)+1 - same as createDesignServices. Plain INSERT relies on
              // BIGSERIAL whose sequence often lags behind after bulk/explicit-ID inserts elsewhere,
              // causing duplicate key on design_services_pkey (23505).
              const insertedServices = await batchInsertWithCustomId(
                'design_services',
                cleanedServicesToAdd
              );

              console.log('Successfully added design services:', insertedServices);
              console.log('=== END ADDING NEW SERVICES ===');
            } else {
              console.log('No new services to add');
            }
            

          } catch (error) {
            console.error('Error updating design services:', error);
            throw error;
          }
        } catch (servicesError) {
          console.error('Failed to update design services:', servicesError);
          throw servicesError; // Don't continue if design services update fails
        }
      }

      if (updates.status && updates.statusHistory?.[0]) {
        const { error: historyError } = await supabase
          .from('status_history')
          .insert({
            project_id: id,
            status: updates.status,
            comment: updates.statusHistory[0].comment,
            user_id: updates.statusHistory[0].userId,
            user_name: updates.statusHistory[0].userName,
            created_at: new Date().toISOString()
          });

        if (historyError) throw historyError;
      }

      // Refetch the project with its managers
      const updatedProject = await get().getProjectById(id);

      set(state => ({
        projects: state.projects.map(p => 
          p.id === id && updatedProject 
            ? updatedProject
            : p
        ),
        selectedProject: state.selectedProject?.id === id && updatedProject
          ? updatedProject
          : state.selectedProject,
        loading: false
      }));
    } catch (error) {
      console.error('Error in updateProject:', error);
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  deleteProject: async (id) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);

      if (error) throw error;

      set(state => ({
        projects: state.projects.filter(p => p.id !== id),
        selectedProject: state.selectedProject?.id === id ? null : state.selectedProject,
        loading: false
      }));
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  setSelectedProject: (project: Project | null) => {
    set({ 
      selectedProject: project,
      error: null
    });
  },

  setCustomers: (customers) => set({ customers }),

  setUsers: (users) => set({
    projectManagers: users.filter(
      (u) => ['project_manager', 'site_manager', 'manager', 'sales'].includes(u.role) && u.isActive
    ),
  }),

  getProjectById: async (id) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!data) throw new Error('Project not found');

      // Fetch project managers
      const { data: projectManagers, error: pmError } = await supabase
        .from('project_managers')
        .select('*')
        .eq('project_id', id);
      
      if (pmError) {
        console.error('Error fetching project managers:', pmError);
        throw pmError;
      }

      // Fetch design services or construction phases
      let designServices = [];
      let constructionPhases = [];

      if (data.type === 'design') {
        const { data: services, error: dsError } = await supabase
          .from('design_services')
          .select('*')
          .eq('project_id', id);
        
        if (dsError) {
          console.error('Error fetching design services:', dsError);
          throw dsError;
        }
        
        // Normalize design service types to match the expected DesignServiceType enum
        designServices = services ? services.map((service: any) => {
          // Ensure we have a valid type
          const serviceType = (service.type || 'other').toLowerCase();
          
          // Normalize to match the enum values
          const validTypes = [
            'floor_plan', '3d_elevation', 'interior_design', '3d_working_elevation',
            'structural_design', 'electrical_design', 'plumbing_design', 'hvac_design',
            'sanction_plan', 'site_visit', 'key_plan',
            'surveying_geotechnical', 'mechanical', 'soil_test', 'borewell_marking', 'other'
          ];
          
          // If not a valid type, default to 'other'
          const normalizedType = validTypes.includes(serviceType) ? serviceType : 'other';
          
          return {
            ...service,
            // Store the normalized type
            type: normalizedType
          };
        }) : [];
        

      } else if (data.type === 'construction') {
        const { data: phases, error: cpError } = await supabase
          .from('construction_phases')
          .select('*')
          .eq('project_id', id);
        
        if (cpError) throw cpError;
        constructionPhases = phases || [];
      }

      // Fetch status history
      const { data: statusHistory, error: shError } = await supabase
        .from('status_history')
        .select('*')
        .eq('project_id', id)
        .order('created_at', { ascending: false });
      
      if (shError) throw shError;

      // Fetch todos
      const { data: todos, error: todosError } = await supabase
        .from('project_todos')
        .select('*')
        .eq('project_id', id)
        .order('created_at', { ascending: false });
      
      if (todosError) throw todosError;

      // Get user details for managers
      const managerIds = projectManagers?.map((pm: any) => pm.user_id) || [];
      
      let managerNames: Record<string, string> = {};
      
      if (managerIds.length > 0) {
        try {
          const { data: users, error: usersError } = await supabase
            .from('users')
            .select('id, name')
            .in('id', managerIds);

          if (usersError) throw usersError;

          managerNames = (users || []).reduce((acc: Record<string, string>, user: any) => {
            acc[user.id] = user.name;
            return acc;
          }, {});
        } catch (err) {
          console.error('Error fetching manager names:', err);
          // Continue even if we can't get manager names
        }
      }

      // Create a record of managers with primary flag
      const projectManagersList = normalizeProjectManagerAssignments(
        (projectManagers || []).map((pm: any) => ({
          id: pm.id,
          userId: pm.user_id,
          name: managerNames[pm.user_id] || 'Unknown',
          isPrimary: pm.is_primary === true,
          assignedAt: pm.created_at ? new Date(pm.created_at) : new Date(),
        })),
        data.manager_id,
      );

      // Format the project data
      const project = {
        id: data.id,
        name: data.name,
        type: data.type,
        status: data.status,
        customerId: data.customer_id,
        customerName: data.customer_name,
        managerId: data.manager_id,
        managerName: data.manager_name,
        startDate: new Date(data.start_date),
        endDate: data.end_date ? new Date(data.end_date) : null,
        expectedDate: data.expected_date ? new Date(data.expected_date) : null,
        deliveredAt: data.delivered_at ? new Date(data.delivered_at) : null,
        completedDate: data.completed_date ? new Date(data.completed_date) : null,
        description: data.description,
        requirementDetails: data.requirement_details
          ? parseLeadRequirementDetails(data.requirement_details)
          : null,
        location: data.location,
        latitude: data.latitude != null ? Number(data.latitude) : null,
        longitude: data.longitude != null ? Number(data.longitude) : null,
        // New site-specific fields
        cornerSite: data.corner_site,
        buildType: data.buildtype, // Using existing buildtype column
        projectCategory: data.project_type, // Using existing project_type column
        siteDimension: data.site_dimension,
        doorFacing: data.door_facing,
        openKitchen: data.open_kitchen,
        parking: data.parking,
        priority: data.priority,
        referenceNumber: data.reference_number ?? null,
        budget: data.budget || 0,
        progress: data.progress || 0,
        spent: data.spent || 0,
        createdAt: new Date(data.created_at),
        updatedAt: data.updated_at ? new Date(data.updated_at) : new Date(data.created_at),
        designServices: designServices.map((ds: any) => {
          const mappedService = {
            id: ds.id,
            type: ds.type, // Using our normalized type
            status: ds.status || 'not_started',
            managerId: ds.manager_id || undefined,
            managerName: ds.manager_name || undefined,
            startDate: ds.start_date ? new Date(ds.start_date) : undefined,
            dueDate: ds.due_date ? new Date(ds.due_date) : undefined,
            endDate: ds.end_date ? new Date(ds.end_date) : undefined,
            completedDate: ds.completed_date ? new Date(ds.completed_date) : null,
            revisionCount: ds.revision_count || 1,
            numberOfTimes: ds.number_of_times || 1,
            comments: []
          };
          return mappedService;
        }),
        phases: constructionPhases.map((phase: any) => ({
          id: phase.id,
          name: phase.name,
          category: phase.category || '',
          duration: phase.duration || '',
          status: phase.status || 'not_started',
          startDate: phase.start_date ? new Date(phase.start_date) : null,
          endDate: phase.end_date ? new Date(phase.end_date) : null,
          completedDate: phase.completed_date ? new Date(phase.completed_date) : null,
          documents: [],
          expenses: [],
          comments: [],
          possibleStatuses: ['not_started', 'in_progress', 'on_hold', 'completed']
        })),
        statusHistory: (statusHistory || []).map((sh: any) => ({
          id: sh.id,
          status: sh.status,
          comment: sh.comment,
          userId: sh.user_id,
          userName: sh.user_name,
          timestamp: new Date(sh.created_at)
        })),
        todos: (todos || []).map((todo: any) => ({
          id: todo.id,
          projectId: todo.project_id,
          title: todo.title,
          description: todo.description,
          dueDate: todo.due_date ? new Date(todo.due_date) : undefined,
          priority: todo.priority,
          status: todo.status,
          createdBy: todo.created_by,
          assignedTo: todo.assigned_to ?? null,
          createdAt: new Date(todo.created_at),
          updatedAt: new Date(todo.updated_at),
          completedAt: todo.completed_at ? new Date(todo.completed_at) : undefined,
          notes: todo.notes
        })),
        documents: [],
        projectManagers: projectManagersList
      };

      set({
        selectedProject: project as unknown as Project,
        loading: false,
        error: null
      });

      return project as unknown as Project;
    } catch (error) {
      console.error('Error in getProjectById:', error);
      const message = (error as Error).message;
      set({ 
        error: message,
        loading: false 
      });
      throw new Error(message);
    }
  },

  updatePhase: async (projectId: number, phaseName: string, updates: PhaseUpdate) => {
    set({ loading: true, error: null });
    try {
      // First verify the phase exists
      const { data: existingPhase, error: fetchError } = await supabase
        .from('construction_phases')
        .select('*')
        .eq('project_id', projectId)
        .eq('name', phaseName)
        .single();

      if (fetchError) throw fetchError;
      if (!existingPhase) throw new Error('Phase not found');

      // Update the phase
      const { error: updateError } = await supabase
        .from('construction_phases')
        .update({
          status: updates.status,
          completed_date: updates.status === 'completed' ? new Date().toISOString() : null
        })
        .eq('project_id', projectId)
        .eq('name', phaseName);

      if (updateError) throw updateError;

      const { data: phases, error: phasesError } = await supabase
        .from('construction_phases')
        .select('status')
        .eq('project_id', projectId);

      if (phasesError) throw phasesError;

      const { data: tasks, error: tasksError } = await supabase
        .from('construction_phase_tasks')
        .select('status')
        .eq('project_id', projectId);

      if (tasksError) throw tasksError;

      const progress = computeConstructionProgress(phases ?? [], tasks ?? []).percent;

      const { error: projectError } = await supabase
        .from('projects')
        .update({
          progress,
          updated_at: new Date().toISOString()
        })
        .eq('id', projectId);

      if (projectError) throw projectError;

      // Fetch updated project
      const updatedProject = await get().getProjectById(projectId);
      if (!updatedProject) throw new Error('Failed to fetch updated project');

      // Update local state
      set(state => ({
        projects: state.projects.map(p => 
          p.id === projectId ? updatedProject : p
        ),
        selectedProject: updatedProject,
        loading: false,
        error: null
      }));

    } catch (error) {
      const message = (error as Error).message;
      set({ 
        error: message,
        loading: false 
      });
      throw new Error(message);
    }
  },

  updateService: async (projectId: number, serviceType: string, updates: ServiceUpdate) => {
    try {
      // Create update object with consistent fields
      const updateData = {
        ...(updates.status !== undefined && { status: updates.status }),
        ...(updates.start_date !== undefined && { start_date: updates.start_date }),
        ...(updates.due_date !== undefined && { due_date: updates.due_date }),
        ...(updates.end_date !== undefined && { end_date: updates.end_date }),
        ...(updates.completed_date !== undefined && { completed_date: updates.completed_date }),
        ...(updates.revision_count !== undefined && { revision_count: updates.revision_count }),
        ...(updates.number_of_times !== undefined && { number_of_times: updates.number_of_times })
      };

      // Handle completed status logic
      if (updates.status === 'completed' && !updates.completed_date) {
        updateData.completed_date = new Date().toISOString();
      }
      // Avoid valid_service_dates violations: stale due_date can sit outside [start, end].
      if (updates.status === 'completed' && updates.due_date === undefined) {
        updateData.due_date = null;
      }

      

      // Update using both type and project_id columns
      const { data, error } = await supabase
        .from('design_services')
        .update(updateData)
        .eq('type', serviceType)
        .eq('project_id', projectId)
        .select();
      
      if (error) {
        console.error('Error updating design service:', error);
        throw error;
      }

      

      // Fetch the full project data to ensure all relationships (including managers) are up to date
      const updatedProject = await get().getProjectById(projectId);
      
      if (!updatedProject) {
        throw new Error('Failed to fetch updated project data');
      }

      // Update the state with the fresh project data
      set(state => ({
        // Update the projects array with the freshly fetched project
        projects: state.projects.map(p => p.id === projectId ? updatedProject : p),
        // Update the selected project if it's the one being modified
        selectedProject: state.selectedProject?.id === projectId ? updatedProject : state.selectedProject
      }));
    } catch (error) {
      console.error('Error updating service:', error);
      throw error;
    }
  },

  updateProjectStatus: async (projectId: number, newStatus: Project['status'], comment: string, userId: string, userName: string, endDate?: Date | null) => {
    try {
      // Prepare update data with end_date when status is completed
      const updateData: { status: string; end_date?: string | null } = { 
        status: newStatus
      };
      
      // Add end date when project is marked as completed
      if (newStatus === 'completed' && endDate) {
        updateData.end_date = endDate.toISOString();
      } else if (newStatus !== 'completed') {
        // Clear end date if project is not completed
        updateData.end_date = null;
      }
      
      const { error: projectError } = await supabase
        .from('projects')
        .update(updateData)
        .eq('id', projectId);

      if (projectError) throw projectError;

      // Create a new status history entry
      const { error: historyError } = await supabase
        .from('status_history')
        .insert({
          project_id: projectId,
          status: newStatus,
          comment,
          user_id: userId,
          user_name: userName,
          created_at: new Date().toISOString()
        });

      if (historyError) throw historyError;

      // Fetch the full project data to ensure all relationships (including managers) are up to date
      const updatedProject = await get().getProjectById(projectId);
      
      if (!updatedProject) {
        throw new Error('Failed to fetch updated project data');
      }

      // Update the state with the fresh project data
      set(state => ({
        // Update the projects array with the freshly fetched project
        projects: state.projects.map(p => p.id === projectId ? updatedProject : p),
        // Update the selected project if it's the one being modified
        selectedProject: state.selectedProject?.id === projectId ? updatedProject : state.selectedProject
      }));
    } catch (error) {
      throw error;
    }
  },

  addTodo: async (todoData: CreateTodoData) => {
    set({ loading: true, error: null });
    try {
      const user = useAuthStore.getState().user;
      if (!user) throw new Error('User not authenticated');

      const { data: todo, error } = await supabase
        .from('project_todos')
        .insert({
          project_id: todoData.projectId,
          title: todoData.title,
          description: todoData.description || null,
          due_date: todoData.dueDate?.toISOString().split('T')[0] || null,
          priority: todoData.priority,
          created_by: user.id,
          notes: todoData.notes || null,
          assigned_to: todoData.assignedTo ?? null,
        })
        .select()
        .single();

      if (error) throw error;

      // Refresh the project to get updated todos
      const updatedProject = await get().getProjectById(todoData.projectId);
      if (updatedProject) {
        set(state => ({
          projects: state.projects.map(p => 
            p.id === todoData.projectId ? updatedProject : p
          ),
          selectedProject: state.selectedProject?.id === todoData.projectId 
            ? updatedProject 
            : state.selectedProject,
          loading: false
        }));
      }
    } catch (error) {
      console.error('Error adding todo:', error);
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  updateTodo: async (todoId: number, updates: UpdateTodoData) => {
    set({ loading: true, error: null });
    try {
      const updateData: any = {};
      
      if (updates.title !== undefined) updateData.title = updates.title;
      if (updates.description !== undefined) updateData.description = updates.description;
      if (updates.dueDate !== undefined) updateData.due_date = updates.dueDate?.toISOString().split('T')[0] || null;
      if (updates.priority !== undefined) updateData.priority = updates.priority;
      if (updates.status !== undefined) updateData.status = updates.status;
      if (updates.notes !== undefined) updateData.notes = updates.notes;
      if ('assignedTo' in updates) updateData.assigned_to = updates.assignedTo ?? null;

      // Set completed_at when status is completed
      if (updates.status === 'completed') {
        updateData.completed_at = new Date().toISOString();
      } else if (updates.status) {
        updateData.completed_at = null;
      }

      const { data: todo, error } = await supabase
        .from('project_todos')
        .update(updateData)
        .eq('id', todoId)
        .select()
        .single();

      if (error) throw error;

      // Refresh the project to get updated todos
      const updatedProject = await get().getProjectById(todo.project_id);
      if (updatedProject) {
        set(state => ({
          projects: state.projects.map(p =>
            p.id === todo.project_id ? updatedProject : p
          ),
          selectedProject: state.selectedProject?.id === todo.project_id
            ? updatedProject
            : state.selectedProject,
          loading: false
        }));
      }
    } catch (error) {
      console.error('Error updating todo:', error);
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  },

  deleteTodo: async (todoId: number) => {
    set({ loading: true, error: null });
    try {
      // First get the todo to know which project it belongs to
      const { data: todo, error: fetchError } = await supabase
        .from('project_todos')
        .select('project_id')
        .eq('id', todoId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('project_todos')
        .delete()
        .eq('id', todoId);

      if (error) throw error;

      // Refresh the project to get updated todos
      if (todo) {
        const updatedProject = await get().getProjectById(todo.project_id);
        if (updatedProject) {
          set(state => ({
            projects: state.projects.map(p => 
              p.id === todo.project_id ? updatedProject : p
            ),
            selectedProject: state.selectedProject?.id === todo.project_id 
              ? updatedProject 
              : state.selectedProject,
            loading: false
          }));
        }
      }
    } catch (error) {
      console.error('Error deleting todo:', error);
      set({ error: (error as Error).message, loading: false });
      throw error;
    }
  }
}));

export type { CreateProjectData, UpdateProjectData };